import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import { announceSessionEnded, sessionEndedMessage } from "../../../store/actions/userActions";

// Athlete Mode V2 API client. Same conventions as the brand services: JWT
// from localStorage, errors unwrapped to `HTTP <status>: <detail>`, where
// the detail is the server's plain `message` when it sent one.

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export const unwrapError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    const detail = data
      ? data.message || data.error || data.detail || JSON.stringify(data)
      : error.response.statusText;
    // 401: the session is over (account locked or deleted, token expired).
    // Sign out to the login page with the plain message instead of leaving
    // a raw "HTTP 401" on the screen.
    if (status === 401 && localStorage.getItem("token")) {
      announceSessionEnded(sessionEndedMessage(status, data));
    }
    const err = new Error(`HTTP ${status}: ${detail}`);
    err.status = status;
    err.data = data;
    throw err;
  }
  throw error;
};

const get = async (path, params) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${path}`, { headers: authHeaders(), params });
    return response.data;
  } catch (error) {
    return unwrapError(error);
  }
};

const send = async (method, path, body) => {
  try {
    const response = await axios({ method, url: `${API_BASE_URL}${path}`, data: body, headers: authHeaders() });
    return response.data;
  } catch (error) {
    return unwrapError(error);
  }
};

// -------------------------------------------------------------------- Home
export const fetchAthleteHome = () => get("/athlete/home/");
export const fetchAthleteBadges = () => get("/athlete/badges/");

// ------------------------------------------------------------------- Deals
export const fetchAthleteDeals = () => get("/athlete/deals/");
export const fetchAthleteDeal = (dealId) => get(`/athlete/deals/${dealId}/`);
// action: accept | decline | counter (+ counter fields)
export const respondToOffer = (dealId, payload) => send("post", `/athlete/deals/${dealId}/respond/`, payload);
export const requestContractChanges = (dealId, note) =>
  send("post", `/athlete/deals/${dealId}/request-changes/`, { note });
export const signContract = (dealId) => send("post", `/athlete/deals/${dealId}/sign/`);
export const submitContent = (dealId, payload) => send("post", `/athlete/deals/${dealId}/submit/`, payload);
export const athleteContractDownloadUrl = (dealId) => `${API_BASE_URL}/athlete/deals/${dealId}/contract/download/`;

export const downloadAthleteContract = async (dealId, fallbackName = "contract") => {
  try {
    const response = await axios.get(athleteContractDownloadUrl(dealId), {
      headers: authHeaders(),
      responseType: "blob",
    });
    const disposition = response.headers["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = match ? match[1] : `${fallbackName}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    return unwrapError(error);
  }
  return null;
};

// ---------------------------------------------------------------- Messages
export const fetchAthleteThreads = () => get("/athlete/messages/");
export const openAthleteThread = (dealId) => send("post", "/athlete/messages/", { dealId });
export const fetchAthleteThread = (threadId) => get(`/athlete/messages/${threadId}/`);
export const sendAthleteMessage = (threadId, text, attachment) =>
  send("post", `/athlete/messages/${threadId}/`, { text, attachment });

// ------------------------------------------------------------ Circle/Atlas
export const fetchCircleBrands = () => get("/athlete/circle/brands/");
export const fetchAtlas = () => get("/athlete/atlas/");
export const fetchAtlasMatches = (email) => get("/athlete/brand-matches/", { email });

// ----------------------------------------------------------------- Guardian
export const fetchCoSignQueue = () => get("/circle/cosign/");
export const guardianCoSign = (dealId) => send("post", `/circle/cosign/${dealId}/`);
